/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { MODE_GAP_MS, STATUS_BELOW_PAD_PX, STATUS_GRID_GAP, STATUS_ROW_PX } from './constants'
import type { AssetHistory, ReeferSample } from './types'

/** The collapsible sections under the graph, in display order (Fresh's order). */
export type StatusSection = 'zoneMode' | 'reefer' | 'alarms' | 'movement' | 'geofences'

export const STATUS_SECTION_LABELS: Record<StatusSection, string> = {
  zoneMode: 'Zone Mode',
  reefer: 'Reefer',
  alarms: 'Alarms',
  movement: 'Movement',
  geofences: 'Geofences',
}

/** A status lane drawn inside the temperature grid. */
export interface StatusLane {
  key: string
  label: string
}

/** A row in the grid under the graph: a section header, or one lane of bars. */
export interface StatusRow {
  key: string
  label: string
  section: StatusSection
  header?: boolean
}

export interface StatusLayout {
  /** Lanes drawn inside the temperature grid, top to bottom. */
  inGraph: StatusLane[]
  /** Visible rows under the graph: every section's header, plus the rows of sections that aren't collapsed. */
  below: StatusRow[]
  /** True when there are lanes the "Status bars" toggle can move (power, movement, zone mode, alarms). */
  hasMovableLanes: boolean
}

/** The reefer detail fields that only ever show under the graph. Power State is drawn
 *  on the graph (or first in this section when status bars are below), so it isn't here. */
export const REEFER_DETAIL_ROWS = [
  { field: 'powerMode', label: 'Power Mode' },
  { field: 'operation', label: 'Operation' },
  { field: 'powerSource', label: 'Power Source' },
  { field: 'engineSpeed', label: 'Engine Speed' },
  { field: 'airControl', label: 'Air Control' },
] as const

export type ReeferDetailField = (typeof REEFER_DETAIL_ROWS)[number]['field']

export const reeferRowKey = (field: ReeferDetailField) => `reefer:${field}`
export const geofenceRowKey = (name: string) => `geofence:${name}`

/** Distinct geofence names in order of first entry. Empty when geofences are hidden. */
export const geofenceNames = (data: AssetHistory, hideGeofences: boolean): string[] =>
  hideGeofences ? [] : [...new Set((data.geofences ?? []).map((visit) => visit.name))]

/**
 * Which status lanes go in the temperature grid and which rows go under it. The one place
 * that decides, so the chart options and the container height can't disagree.
 *
 * - On graph: power, movement, zone mode and alarms stay in the graph; the reefer detail
 *   rows and the per-geofence rows go under it.
 * - Below: everything goes under the graph, grouped Zone Mode, Reefer, Alarms, Movement.
 *
 * A section with no rows is left out entirely; a collapsed one shows only its header.
 */
export const buildStatusLayout = (
  data: AssetHistory,
  statusBarsBelow: boolean,
  collapsed: ReadonlySet<string>,
  hideGeofences: boolean
): StatusLayout => {
  const power: StatusLane[] = (data.powerState ?? []).length ? [{ key: 'power', label: 'Power State' }] : []
  const movement: StatusLane[] = (data.movement ?? []).length ? [{ key: 'movement', label: 'Movement' }] : []
  const zoneModes: StatusLane[] = Object.entries(data.zones)
    .filter(([, zone]) => (zone?.mode?.length ?? 0) > 0)
    .map(([zoneName]) => ({ key: `zoneMode:${zoneName}`, label: `${zoneName} Mode` }))
  const alarms: StatusLane[] = (data.alarms ?? [])
    .filter((alarm) => alarm.times.length > 0)
    .map((alarm, i) => ({
      key: `alarm:${i}`,
      label: [alarm.code, alarm.description].filter(Boolean).join(': ') || `Alarm ${i + 1}`,
    }))
  const reeferDetails: StatusLane[] = REEFER_DETAIL_ROWS.filter(({ field }) => (data[field] ?? []).length > 0).map(
    ({ field, label }) => ({ key: reeferRowKey(field), label })
  )
  const geofences: StatusLane[] = geofenceNames(data, hideGeofences).map((name) => ({
    key: geofenceRowKey(name),
    label: name,
  }))

  const sections: [StatusSection, StatusLane[]][] = statusBarsBelow
    ? [
        ['zoneMode', zoneModes],
        ['reefer', [...power, ...reeferDetails]],
        ['alarms', alarms],
        ['movement', [...movement, ...geofences]],
      ]
    : [
        ['reefer', reeferDetails],
        ['geofences', geofences],
      ]

  const below: StatusRow[] = []
  for (const [section, lanes] of sections) {
    if (!lanes.length) continue
    below.push({ key: `section:${section}`, label: STATUS_SECTION_LABELS[section], section, header: true })
    if (!collapsed.has(section)) {
      below.push(...lanes.map((lane) => ({ ...lane, section })))
    }
  }

  const movable = [...power, ...movement, ...zoneModes, ...alarms]
  return {
    inGraph: statusBarsBelow ? [] : movable,
    below,
    hasMovableLanes: movable.length > 0,
  }
}

/** Pixel height the grid under the graph adds to the chart, including its gap and padding. */
export const belowGridHeight = (rowCount: number): number =>
  rowCount > 0 ? STATUS_GRID_GAP + rowCount * STATUS_ROW_PX + STATUS_BELOW_PAD_PX : 0

export interface StateSegment {
  start: number
  end: number
  value: string
}

/**
 * Run-length segments for a reefer state stream: one segment per run of equal values, ending
 * at the first sample with a different value. Like the zone-mode bar, a silence longer than
 * MODE_GAP_MS means the unit wasn't reporting, so the run ends at its last sample and the row
 * shows a hole. A blank value is no data. The last run extends to `visEnd` (now, or the window
 * end) unless the stream went silent before then.
 */
export const buildStateSegments = (samples: ReeferSample[], visEnd: number): StateSegment[] => {
  const segments: StateSegment[] = []
  let run: StateSegment | null = null
  let lastTime = 0
  const close = (end: number) => {
    if (run) segments.push({ ...run, end })
    run = null
  }
  for (const sample of samples) {
    const time = new Date(sample.time).getTime()
    if (run && time - lastTime > MODE_GAP_MS) {
      close(lastTime)
    }
    if (!sample.value) {
      close(time)
    } else if (!run) {
      run = { start: time, end: time, value: sample.value }
    } else if (run.value !== sample.value) {
      close(time)
      run = { start: time, end: time, value: sample.value }
    }
    lastTime = time
  }
  close(visEnd - lastTime > MODE_GAP_MS ? lastTime : Math.max(lastTime, visEnd))
  return segments
}
